/**
 * Numeric `number` primitive for the Monitor wizard.
 *
 * Renders a single bounded number input for capability fields declared as
 * `type: number` — currently DataEQ's `record_limit` (how many mentions to
 * pull per run). The field's `min`/`max` become the input's native bounds and
 * an inline "min–max" hint, but they are advisory: the backend re-clamps the
 * submitted value into range regardless of what reaches it, so a pasted or
 * spinner-nudged out-of-range value can't produce a runaway or empty run.
 *
 * The value is forwarded into source_config as a number (not a string) so the
 * saved job carries a real integer. An empty input forwards `undefined`, which
 * lets the backend fall back to its own default rather than persisting 0.
 */
import React from 'react';

import { Input } from '../../../../ui/input';
import { Label } from '../../../../ui/label';
import type { CapabilityField } from '../../../../../types/importCapabilities';

interface NumberFieldProps {
  field: CapabilityField;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}

const NumberField: React.FC<NumberFieldProps> = ({ field, value, onChange }) => {
  const fieldId = `field-${field.id}`;
  const fallback =
    typeof field.default === 'number' ? field.default : undefined;

  // Controlled from the parent's value; show the declared default as the
  // placeholder so an untouched field reads as "will use N" rather than blank.
  const shown = typeof value === 'number' ? String(value) : '';

  const hint = field.help ?? field.description;
  const rangeHint =
    typeof field.min === 'number' && typeof field.max === 'number'
      ? `${field.min}–${field.max}`
      : null;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={fieldId}>
        {field.label || field.id}
        {field.required ? <span className="ml-1 text-red-500">*</span> : null}
      </Label>
      {hint ? <p className="text-xs text-slate-500">{hint}</p> : null}
      <Input
        id={fieldId}
        type="number"
        inputMode="numeric"
        min={field.min}
        max={field.max}
        step={field.step ?? 1}
        value={shown}
        placeholder={fallback !== undefined ? String(fallback) : undefined}
        onChange={(e) => {
          const raw = e.target.value.trim();
          if (raw === '') {
            onChange(undefined);
            return;
          }
          const parsed = Number(raw);
          onChange(Number.isFinite(parsed) ? parsed : undefined);
        }}
      />
      {rangeHint ? (
        <p className="text-xs text-slate-400">Allowed range: {rangeHint}</p>
      ) : null}
    </div>
  );
};

export default NumberField;
